import moment from "moment";
import { useOutletContext } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Grid, Switch } from "@mui/material";

import CustomDateRangePicker from "./components/custom-date-range";
import WodByClient from "./components/wod-by-clients";
import WodByProduct from "./components/wod-by-product";
import SalesByPackSize from "./components/sales-by-pack-size";
import SalesByBeerType from "./components/sales-by-beer-type";
import SalesVolumeTrend from "./components/sales-volume-trend";
import TopClientsByVolume from "./components/top-clients-by-volume";
import OrderIntervalsAnalysis from "./components/order-intervals-analysis";
import NoDataFound from "../../../common/dashboard/no-data-found";
import NoDataDialog from "../../../common/dashboard/NoDataDialog";
import SalesCollectionTrend from "./components/sales-collection-trend";
import {
  dashboardAPI,
  KpiFilters,
  ComparisonFilterParams,
} from "../../../api/kpiCardsAPI";
import TopClientsByRevenue from "./components/top-clients-by-revenue";
import OutstandingAgeingReport from "./components/outstanding-aging-report";

import {
  SemiBoldText,
  StyledComparisonFilterContainer,
  StyledPrimaryButton,
  StyledSalesAnalyticsCardsBox,
  StyledSecondaryButton,
  StyledPeriodContainer,
  StyledPeriodLabel,
  StyledValidationErrorContainer,
  StyledValidationErrorText,
  LeftContainer,
  RightContainer,
  SalesAnalyticsCardLabel,
  SalesAnalyticsContentBox,
  StyledSalesAnalyticsCard,
} from "./styles";

interface OutletContextType {
  globalFilters?: KpiFilters;
  comparisonFiltersDisabled: boolean;
  setComparisonFiltersDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  globalFiltersDisabled: boolean;
  setGlobalFiltersDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  skipSalesAnalyticsAPI?: boolean;
  currentTab?:
    | "sales-analytics"
    | "salesrep-analytics"
    | "client-analytics"
    | "finance-analytics";
  dashboardType?: string;
  sellerEmail?: string;
  customerEmail?: string;
}

const salesAnalyticsCardsKeys = {
  salesVolumeTrend: "salesVolumeTrend",
  salesByBeerType: "salesByBeerType",
  salesByPackSize: "salesByPackSize",
  orderIntervalsAnalysis: "orderIntervalsAnalysis",
  topClientsByVolume: "topClientsByVolume",
  wodByProduct: "wodByProduct",
  wodByClient: "wodByClient",
  salesCollectionTrend: "salesRevenueTrend",
  paymentIntervalAnalysis: "paymentIntervalAnalysis",
  outstandingByClient: "outstandingByClient",
  topClientsByRevenue: "topClientsByRevenue",
};

const clientMgmtCardsKeys = {
  salesVolumeTrend: "salesVolumeTrend",
  salesByBeerType: "salesByBeerType",
  salesByPackSize: "salesByPackSize",
  salesCollectionTrend: "salesRevenueTrend",
};

export default function SalesAnalyticsDashboard() {
  const {
    globalFilters,
    comparisonFiltersDisabled,
    setComparisonFiltersDisabled,
    globalFiltersDisabled,
    setGlobalFiltersDisabled,
    skipSalesAnalyticsAPI,
    currentTab = "sales-analytics",
    dashboardType: parentDashboardType,
    sellerEmail,
    customerEmail,
  } = useOutletContext<OutletContextType>();
  const [showValidation, setShowValidation] = useState(false);
  const [period1From, setPeriod1From] = useState<Date | undefined>(undefined);
  const [period2From, setPeriod2From] = useState<Date | undefined>(undefined);
  const [period1To, setPeriod1To] = useState<Date | undefined>(undefined);
  const [period2To, setPeriod2To] = useState<Date | undefined>(undefined);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [appliedComparisonFilter, setAppliedComparisonFilter] = useState<
    ComparisonFilterParams | undefined
  >(undefined);
  const [comparisonFilterEnabled, setComparisonFilterEnabled] = useState(false);
  const [showComparisonControls, setShowComparisonControls] = useState(false);
  const [cardsData, setCardsData] = useState<{ [key: string]: any }>({});
  const [showNoDataPopup, setShowNoDataPopup] = useState(false);
  const userRole = localStorage.getItem("userRole") || undefined;
  const cardsReqIdRef = useRef(0);

  const getDashboardType = (): string => {
    if (parentDashboardType) return parentDashboardType;

    if (userRole === "maltora-sales-rep") return "maltora-sales-rep";

    if (userRole === "maltora-seller") {
      return currentTab === "salesrep-analytics"
        ? "salesrep-analytics"
        : "sales-analytics";
    }
    return "sales-analytics";
  };

  const dashboardType = getDashboardType();
  const isClientManagement = dashboardType === "client-analytics";
  const isFinanceDashboard = dashboardType === "finance-analytics";
  const canFetch = isClientManagement ? !!customerEmail : true;

  useEffect(() => {
    if (globalFilters && !comparisonFiltersDisabled) {
      setComparisonFiltersDisabled(true);
      setAppliedComparisonFilter(undefined);
      setComparisonFilterEnabled(false);
      setShowValidation(false);
      setValidationErrors([]);
      setPeriod1From(undefined);
      setPeriod1To(undefined);
      setPeriod2From(undefined);
      setPeriod2To(undefined);
    }
  }, [globalFilters]);

  const fetchChartData = useCallback(
    async (comparisonFilterOverride?: ComparisonFilterParams | null) => {
      if (!canFetch || skipSalesAnalyticsAPI) {
        setCardsData({});
        return;
      }

      const reqId = ++cardsReqIdRef.current;

      try {
        const comparisonFilterToUse =
          comparisonFilterOverride === null
            ? undefined
            : (comparisonFilterOverride ?? appliedComparisonFilter);

        const dashboardData = await dashboardAPI.getKPICardsWithComparison(
          globalFilters,
          isFinanceDashboard ? undefined : sellerEmail,
          dashboardType,
          comparisonFilterToUse,
          isFinanceDashboard || isClientManagement ? customerEmail : undefined
        );

        if (reqId !== cardsReqIdRef.current) return;

        const cardsByKey = dashboardData.reduce<Record<string, any>>(
          (acc, card) => {
            const normalizedKey =
              card.key === "salesBeerType"
                ? "salesByBeerType"
                : card.key === "orderIntervalAnalysis" ||
                    card.key === "orderIntervalsAnalysis"
                  ? "orderIntervalsAnalysis"
                  : card.key === "salesCollectionTrend"
                    ? "salesRevenueTrend"
                    : card.key;

            acc[normalizedKey] = transformAPIDataToComponentFormat({
              ...card,
              key: normalizedKey,
            });
            return acc;
          },
          {}
        );

        setCardsData(cardsByKey);
      } catch (error: any) {
        if (reqId !== cardsReqIdRef.current) return;
        console.error("Failed to load KPI cards:", error?.message || error);
      }
    },
    [
      canFetch,
      skipSalesAnalyticsAPI,
      globalFilters,
      sellerEmail,
      dashboardType,
      appliedComparisonFilter,
      isFinanceDashboard,
      isClientManagement,
      customerEmail,
    ]
  );

  const anyFiltersActive = useMemo(() => {
    const hasGlobalFilters =
      globalFilters &&
      Object.values(globalFilters).some((v) => v !== undefined && v !== "");
    return hasGlobalFilters || comparisonFilterEnabled;
  }, [globalFilters, comparisonFilterEnabled]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const getPeriodPattern = (
    from: Date,
    to: Date
  ): "day" | "week" | "month" | "year" | "custom" => {
    const start = moment(from);
    const end = moment(to);
    const days = end.diff(start, "days") + 1;

    if (days === 1) return "day";
    if (start.clone().add(6, "days").isSame(end, "day") && start.day() === 1)
      return "week";
    if (start.date() === 1 && end.isSame(start.clone().endOf("month"), "day"))
      return "month";
    if (
      start.month() === 0 &&
      start.date() === 1 &&
      end.isSame(start.clone().endOf("year"), "day")
    )
      return "year";

    return "custom";
  };

  const haveSameDatePattern = (
    p1From: Date,
    p1To: Date,
    p2From: Date,
    p2To: Date
  ): boolean => {
    const pattern1 = getPeriodPattern(p1From, p1To);
    const pattern2 = getPeriodPattern(p2From, p2To);

    return pattern1 === pattern2;
  };

  const hasValidComparisonPeriods = useMemo(() => {
    return !!(period1From && period1To && period2From && period2To);
  }, [period1From, period1To, period2From, period2To]);

  const validateDateRanges = (): string[] => {
    const errors: string[] = [];

    if (!period1From || !period1To)
      errors.push("Period 1 requires both start and end dates");
    if (!period2From || !period2To)
      errors.push("Period 2 requires both start and end dates");

    if (period1From && period1To && period1From >= period1To)
      errors.push("Period 1 start date must be before end date");
    if (period2From && period2To && period2From >= period2To)
      errors.push("Period 2 start date must be before end date");

    if (
      !isClientManagement &&
      period1From &&
      period1To &&
      period2From &&
      period2To
    ) {
      if (
        !haveSameDatePattern(period1From, period1To, period2From, period2To)
      ) {
        errors.push(
          "Periods must have same date pattern (e.g., similar day/week/month/year ranges)"
        );
      }
    }

    return errors;
  };

  const formatDateForAPI = (date: Date): string =>
    moment(date).format("YYYY-MM-DD");

  function transformAPIDataToComponentFormat(apiData: any): any {
    if (!apiData || !apiData.details) return apiData;

    if (
      apiData.key === salesAnalyticsCardsKeys.outstandingByClient &&
      Array.isArray(apiData.details)
    ) {
      const negativeOnlySorted = apiData.details
        .filter((c: any) => Number(c?.grandTotal) < 0)
        .map((c: any) => {
          const absVal = Math.abs(Number(c?.grandTotal) || 0);

          return {
            client_name: c?.outletName ?? "-",
            pincode: String(c?.pincode ?? "-"),
            revenue: String(absVal),
            previous_month_percent: c?.growth
              ? `${String(c.growth)} vs last month`
              : undefined,
          };
        })
        .sort((a: any, b: any) => Number(b.revenue) - Number(a.revenue));

      return {
        ...apiData,
        name: "Clients Outstanding List",
        details: [
          {
            period: "current",
            name: "Current",
            values: negativeOnlySorted,
          },
        ],
      };
    }

    if (apiData.key === salesAnalyticsCardsKeys.paymentIntervalAnalysis) {
      const transformedDetails = apiData.details.map((detail: any) => {
        if (detail.resultValues && !detail.values) {
          return {
            ...detail,
            values: detail.resultValues,
          };
        }
        return detail;
      });

      return {
        ...apiData,
        details: transformedDetails,
      };
    }

    if (apiData.key === salesAnalyticsCardsKeys.orderIntervalsAnalysis) {
      return apiData;
    }

    if (
      apiData.key === salesAnalyticsCardsKeys.topClientsByVolume &&
      Array.isArray(apiData.details)
    ) {
      return apiData;
    }

    if (
      typeof apiData.details === "object" &&
      !Array.isArray(apiData.details)
    ) {
      const transformedDetails: any[] = [];

      if (apiData.details.current) {
        transformedDetails.push({
          period: "current",
          name: "Current",
          values: apiData.details.current,
        });
      }

      if (apiData.details.period_one) {
        let period1Values = apiData.details.period_one;

        if (apiData.details.period_one.values !== undefined) {
          period1Values = apiData.details.period_one.values;
          if (
            period1Values &&
            typeof period1Values === "object" &&
            period1Values.products
          ) {
            period1Values = period1Values.products;
          }
        }

        if (!Array.isArray(period1Values)) period1Values = [];

        transformedDetails.push({
          period: "one",
          name: "Period 1",
          values: period1Values,
        });
      }

      if (apiData.details.period_two) {
        let period2Values = apiData.details.period_two;

        if (apiData.details.period_two.values !== undefined) {
          period2Values = apiData.details.period_two.values;
        }

        if (!Array.isArray(period2Values)) period2Values = [];

        transformedDetails.push({
          period: "two",
          name: "Period 2",
          values: period2Values,
        });
      }

      return { ...apiData, details: transformedDetails };
    }

    return apiData;
  }

  const handleApplyFilters = async () => {
    if (!canFetch) return;

    const errors = validateDateRanges();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowValidation(true);
      return;
    }

    if (!period1From || !period1To || !period2From || !period2To) {
      setValidationErrors(["All date ranges must be selected"]);
      setShowValidation(true);
      return;
    }

    const compFilterParams: ComparisonFilterParams = {
      period_one: {
        startDate: formatDateForAPI(period1From),
        endDate: formatDateForAPI(period1To),
      },
      period_two: {
        startDate: formatDateForAPI(period2From),
        endDate: formatDateForAPI(period2To),
      },
      card_type: [],
    };

    setValidationErrors([]);
    setShowValidation(false);
    setAppliedComparisonFilter(compFilterParams);
    setComparisonFilterEnabled(true);
    setGlobalFiltersDisabled(true);

    await fetchChartData(compFilterParams);
  };

  const handleResetFilters = () => {
    setAppliedComparisonFilter(undefined);
    setComparisonFilterEnabled(false);
    setGlobalFiltersDisabled(false);
    setShowValidation(false);
    setValidationErrors([]);
    setPeriod1From(undefined);
    setPeriod1To(undefined);
    setPeriod2From(undefined);
    setPeriod2To(undefined);

    fetchChartData(null);
  };

  const periodValues = useMemo(() => {
    if (!comparisonFilterEnabled) return undefined;

    const formatDate = (date: Date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    };

    return {
      "Period 1":
        period1From && period1To
          ? `Period 1\n(${formatDate(period1From)} - ${formatDate(period1To)})`
          : "Period 1",
      "Period 2":
        period2From && period2To
          ? `Period 2\n(${formatDate(period2From)} - ${formatDate(period2To)})`
          : "Period 2",
    };
  }, [comparisonFilterEnabled, period1From, period1To, period2From, period2To]);

  const hasDataForCard = useCallback(
    (cardKey: string): boolean => {
      const cardData = cardsData[cardKey];
      if (!cardData) return false;

      if (comparisonFilterEnabled) {
        return !!(
          cardData.isPeriodOneDataExists || cardData.isPeriodTwoDataExists
        );
      }
      if (cardKey === salesAnalyticsCardsKeys.paymentIntervalAnalysis) {
        return (
          cardData.details?.filter(
            (d: any) =>
              d.period === "current" &&
              Array.isArray(d.resultValues) &&
              d.resultValues.length > 0
          ).length > 0
        );
      }

      // Special handling for sales volume and collection trends
      if (cardKey === salesAnalyticsCardsKeys.salesVolumeTrend) {
        return (
          cardData.details?.some((d: any) => {
            if (d.period !== "current" || !Array.isArray(d.values))
              return false;
            return d.values.some((value: any) => {
              const vol = Number(value.vol || 0);
              return vol >= 0; // Changed from > 0 to >= 0 to display zero values
            });
          }) || false
        );
      }

      if (cardKey === salesAnalyticsCardsKeys.salesCollectionTrend) {
        return (
          cardData.details?.some((d: any) => {
            if (d.period !== "current" || !Array.isArray(d.values))
              return false;
            return d.values.some((value: any) => {
              const revenue = Number(value.revenue || value.vol || 0);
              return revenue >= 0; // Changed from > 0 to >= 0 to display zero values
            });
          }) || false
        );
      }

      return (
        cardData.details?.filter(
          (d: any) =>
            d.period === "current" &&
            Array.isArray(d.values) &&
            d.values.length > 0
        ).length > 0
      );
    },
    [cardsData, comparisonFilterEnabled]
  );

  const createNoDataCard = useCallback(
    (title: string) => (
      <NoDataFound
        title={title}
        CardComponent={StyledSalesAnalyticsCard}
        CardLabelComponent={SalesAnalyticsCardLabel}
        CardContentComponent={SalesAnalyticsContentBox}
      />
    ),
    []
  );

  const cardsForMode = useMemo(() => {
    // Client Management: Only 3 charts
    if (isFinanceDashboard) {
      if (comparisonFilterEnabled) {
        return [
          {
            key: salesAnalyticsCardsKeys.salesCollectionTrend,
            component: hasDataForCard(
              salesAnalyticsCardsKeys.salesCollectionTrend
            ) ? (
              <SalesCollectionTrend
                comparisonFilterEnabled={comparisonFilterEnabled}
                data={cardsData[salesAnalyticsCardsKeys.salesCollectionTrend]}
                periodValues={periodValues}
                label="Collection Trend"
              />
            ) : (
              createNoDataCard("Collection Trend")
            ),
          },
        ];
      }

      const cards = [
        {
          key: salesAnalyticsCardsKeys.salesCollectionTrend,
          component: hasDataForCard(
            salesAnalyticsCardsKeys.salesCollectionTrend
          ) ? (
            <SalesCollectionTrend
              comparisonFilterEnabled={comparisonFilterEnabled}
              data={cardsData[salesAnalyticsCardsKeys.salesCollectionTrend]}
              periodValues={periodValues}
              label="Collection Trend"
            />
          ) : (
            createNoDataCard("Collection Trend")
          ),
        },
      ];

      cards.push({
        key: "outstandingAgingReport",
        component: <OutstandingAgeingReport />,
      });

      if (!customerEmail) {
        cards.push({
          key: salesAnalyticsCardsKeys.outstandingByClient,
          component: hasDataForCard(
            salesAnalyticsCardsKeys.outstandingByClient
          ) ? (
            <TopClientsByRevenue
              data={cardsData[salesAnalyticsCardsKeys.outstandingByClient]}
              hideGrowthIndicator={anyFiltersActive}
            />
          ) : (
            createNoDataCard("Clients Outstanding List")
          ),
        });

        cards.push({
          key: salesAnalyticsCardsKeys.topClientsByRevenue,
          component: hasDataForCard(
            salesAnalyticsCardsKeys.topClientsByRevenue
          ) ? (
            <TopClientsByRevenue
              data={cardsData[salesAnalyticsCardsKeys.topClientsByRevenue]}
              hideGrowthIndicator={anyFiltersActive}
            />
          ) : (
            createNoDataCard("Top Clients by Revenue")
          ),
        });
      }

      return cards;
    }
    if (isClientManagement) {
      return [
        {
          key: clientMgmtCardsKeys.salesVolumeTrend,
          label: "Sales Volume Trend",
          component: hasDataForCard(clientMgmtCardsKeys.salesVolumeTrend) ? (
            <SalesVolumeTrend
              comparisonFilterEnabled={comparisonFilterEnabled}
              data={cardsData[clientMgmtCardsKeys.salesVolumeTrend]}
              periodValues={periodValues}
            />
          ) : (
            createNoDataCard("Sales Volume Trend")
          ),
        },
        {
          key: clientMgmtCardsKeys.salesCollectionTrend,
          label: "Sales Revenue Trend",
          component: hasDataForCard(
            clientMgmtCardsKeys.salesCollectionTrend
          ) ? (
            <SalesCollectionTrend
              comparisonFilterEnabled={comparisonFilterEnabled}
              data={cardsData[clientMgmtCardsKeys.salesCollectionTrend]}
              periodValues={periodValues}
            />
          ) : (
            createNoDataCard("Sales Revenue Trend")
          ),
        },
        {
          key: clientMgmtCardsKeys.salesByPackSize,
          label: "Sales by Pack Size",
          component: hasDataForCard(clientMgmtCardsKeys.salesByPackSize) ? (
            <SalesByPackSize
              comparisonFilterEnabled={comparisonFilterEnabled}
              data={cardsData[clientMgmtCardsKeys.salesByPackSize]}
              periodValues={periodValues}
            />
          ) : (
            createNoDataCard("Sales by Pack Size")
          ),
        },
        {
          key: clientMgmtCardsKeys.salesByBeerType,
          label: "Sales by Beer Type",
          component: hasDataForCard(clientMgmtCardsKeys.salesByBeerType) ? (
            <SalesByBeerType
              comparisonFilterEnabled={comparisonFilterEnabled}
              data={cardsData[clientMgmtCardsKeys.salesByBeerType]}
              periodValues={periodValues}
            />
          ) : (
            createNoDataCard("Sales by Beer Type")
          ),
        },
      ];
    }

    // Existing Sales Analytics & Sales Rep
    const baseCards = [
      {
        key: salesAnalyticsCardsKeys.salesVolumeTrend,
        component: hasDataForCard(salesAnalyticsCardsKeys.salesVolumeTrend) ? (
          <SalesVolumeTrend
            comparisonFilterEnabled={comparisonFilterEnabled}
            data={cardsData[salesAnalyticsCardsKeys.salesVolumeTrend]}
            periodValues={periodValues}
          />
        ) : (
          createNoDataCard("Sales Volume Trend")
        ),
      },
      {
        key: salesAnalyticsCardsKeys.salesCollectionTrend,
        component: hasDataForCard(
          salesAnalyticsCardsKeys.salesCollectionTrend
        ) ? (
          <SalesCollectionTrend
            comparisonFilterEnabled={comparisonFilterEnabled}
            data={cardsData[salesAnalyticsCardsKeys.salesCollectionTrend]}
            periodValues={periodValues}
          />
        ) : (
          createNoDataCard("Sales Revenue Trend")
        ),
      },
      {
        key: salesAnalyticsCardsKeys.salesByPackSize,
        component: hasDataForCard(salesAnalyticsCardsKeys.salesByPackSize) ? (
          <SalesByPackSize
            comparisonFilterEnabled={comparisonFilterEnabled}
            data={cardsData[salesAnalyticsCardsKeys.salesByPackSize]}
            periodValues={periodValues}
          />
        ) : (
          createNoDataCard("Sales by Pack Size")
        ),
      },
      {
        key: salesAnalyticsCardsKeys.salesByBeerType,
        component: hasDataForCard(salesAnalyticsCardsKeys.salesByBeerType) ? (
          <SalesByBeerType
            comparisonFilterEnabled={comparisonFilterEnabled}
            data={cardsData[salesAnalyticsCardsKeys.salesByBeerType]}
            periodValues={periodValues}
          />
        ) : (
          createNoDataCard("Sales by Beer Type")
        ),
      },
      {
        key: salesAnalyticsCardsKeys.topClientsByVolume,
        component: hasDataForCard(
          salesAnalyticsCardsKeys.topClientsByVolume
        ) ? (
          <TopClientsByVolume
            data={cardsData[salesAnalyticsCardsKeys.topClientsByVolume]}
          />
        ) : (
          createNoDataCard("Top Clients by Volume")
        ),
      },
    ];

    if (comparisonFilterEnabled) {
      const baseWithoutTopClients = baseCards.filter(
        (c) => c.key !== salesAnalyticsCardsKeys.topClientsByVolume
      );
      return [
        ...baseWithoutTopClients,
        {
          key: salesAnalyticsCardsKeys.wodByClient,
          component: hasDataForCard(salesAnalyticsCardsKeys.wodByClient) ? (
            <WodByClient
              data={cardsData[salesAnalyticsCardsKeys.wodByClient]}
              periodValues={periodValues}
            />
          ) : (
            createNoDataCard("WOD By Client")
          ),
        },
        {
          key: salesAnalyticsCardsKeys.wodByProduct,
          component: hasDataForCard(salesAnalyticsCardsKeys.wodByProduct) ? (
            <WodByProduct
              comparisonFilterEnabled={comparisonFilterEnabled}
              data={cardsData[salesAnalyticsCardsKeys.wodByProduct]}
              periodValues={periodValues}
            />
          ) : (
            createNoDataCard("WOD By Product")
          ),
        },
      ];
    }

    return [
      ...baseCards,
      {
        key: salesAnalyticsCardsKeys.wodByProduct,
        component: hasDataForCard(salesAnalyticsCardsKeys.wodByProduct) ? (
          <WodByProduct
            comparisonFilterEnabled={comparisonFilterEnabled}
            data={cardsData[salesAnalyticsCardsKeys.wodByProduct]}
            periodValues={periodValues}
          />
        ) : (
          createNoDataCard("WOD By Product")
        ),
      },
    ];
  }, [
    isFinanceDashboard,
    isClientManagement,
    cardsData,
    comparisonFilterEnabled,
    periodValues,
    hasDataForCard,
    createNoDataCard,
    anyFiltersActive,
  ]);

  const getCardsToDisplay = useMemo(() => {
    if (isFinanceDashboard) return cardsForMode;

    if (isClientManagement) {
      const ordered = [
        clientMgmtCardsKeys.salesVolumeTrend,
        clientMgmtCardsKeys.salesCollectionTrend,
        clientMgmtCardsKeys.salesByPackSize,
        clientMgmtCardsKeys.salesByBeerType,
      ];
      return ordered
        .map((k) => cardsForMode.find((c: any) => c.key === k))
        .filter(Boolean) as any[];
    }

    if (!comparisonFilterEnabled)
      return cardsForMode.filter((card: any) =>
        [
          salesAnalyticsCardsKeys.salesVolumeTrend,
          salesAnalyticsCardsKeys.salesCollectionTrend,
          salesAnalyticsCardsKeys.salesByPackSize,
          salesAnalyticsCardsKeys.salesByBeerType,
          salesAnalyticsCardsKeys.topClientsByVolume,
          salesAnalyticsCardsKeys.wodByProduct,
        ].includes(card.key)
      );

    return cardsForMode;
  }, [
    isFinanceDashboard,
    isClientManagement,
    comparisonFilterEnabled,
    cardsForMode,
  ]);

  const handleNoDataPopupClose = () => {
    setShowNoDataPopup(false);
    handleResetFilters();
  };

  return (
    <>
      <NoDataDialog open={showNoDataPopup} onClose={handleNoDataPopupClose} />

      <StyledComparisonFilterContainer
        style={{
          opacity: comparisonFiltersDisabled ? 0.6 : 1,
          pointerEvents: comparisonFiltersDisabled ? "none" : "auto",
        }}
      >
        <LeftContainer>
          <SalesAnalyticsCardLabel
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <Switch
              checked={showComparisonControls}
              onChange={(e) => {
                const checked = e.target.checked;
                setShowComparisonControls(checked);

                if (!checked) {
                  handleResetFilters();
                }
              }}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#FF6B00",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#FF6B00",
                },
              }}
            />
            Compare periods
          </SalesAnalyticsCardLabel>

          {/* Divider */}
          {showComparisonControls && (
            <div
              style={{
                width: "1px",
                height: "32px",
                backgroundColor: "#E0E0E0",
              }}
            />
          )}

          {showComparisonControls && (
            <>
              <StyledPeriodContainer>
                <StyledPeriodLabel>Period 1</StyledPeriodLabel>
                <CustomDateRangePicker
                  fromDate={period1From}
                  toDate={period1To}
                  setFromDate={setPeriod1From}
                  setToDate={setPeriod1To}
                  disableFuture
                  showReset
                  referenceFromDate={period2From}
                  referenceToDate={period2To}
                  referenceLabel="Period 2"
                />
              </StyledPeriodContainer>

              <StyledPeriodContainer>
                <StyledPeriodLabel>Period 2</StyledPeriodLabel>
                <CustomDateRangePicker
                  fromDate={period2From}
                  toDate={period2To}
                  setFromDate={setPeriod2From}
                  setToDate={setPeriod2To}
                  disableFuture
                  showReset
                  referenceFromDate={period1From}
                  referenceToDate={period1To}
                  referenceLabel="Period 1"
                />
              </StyledPeriodContainer>
            </>
          )}
        </LeftContainer>

        {showComparisonControls && (
          <RightContainer>
            <StyledPrimaryButton
              variant="outlined"
              size="small"
              onClick={handleApplyFilters}
              disabled={
                (isClientManagement && !customerEmail) ||
                !hasValidComparisonPeriods
              }
            >
              Apply
            </StyledPrimaryButton>

            <StyledSecondaryButton
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
              disabled={!comparisonFilterEnabled}
            >
              Reset
            </StyledSecondaryButton>
          </RightContainer>
        )}
      </StyledComparisonFilterContainer>
      {showValidation && validationErrors.length > 0 && (
        <StyledValidationErrorContainer>
          {validationErrors.map((error, index) => (
            <StyledValidationErrorText
              key={index}
              variant="body2"
              sx={{ mb: index < validationErrors.length - 1 ? 1 : 0 }}
            >
              • {error}
            </StyledValidationErrorText>
          ))}
        </StyledValidationErrorContainer>
      )}

      {canFetch && (
        <StyledSalesAnalyticsCardsBox>
          <Grid container spacing={3}>
            {getCardsToDisplay.map((card: any) => {
              if (isClientManagement) {
                const md = 6; // All charts side by side
                return (
                  <Grid item xs={12} md={md} key={card.key}>
                    {card.component}
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} md={6} key={card.key}>
                  {card.component}
                </Grid>
              );
            })}
          </Grid>
        </StyledSalesAnalyticsCardsBox>
      )}
    </>
  );
}
